<script setup lang="ts">
/**
 * 對接商「盤面回放 / 單局詳情」根元件(replay 模式)。
 *
 * 這是跟 App.vue 平行的**另一個 root**:main.ts 開機若 query 帶 record_id + token,
 * 就掛這支(不掛 App.vue)。replay 模式**不碰登入 / Cocos iframe / postMessage**,
 * 只:讀 query → 驗 native game → fetchGameResult → mapResultToRecord → 灌 historyStore
 *     → render 該遊戲的 history 元件(show=true),重用既有 HistoryDetail + 盤面。
 *
 * 🛑 **第一件事一定是 `await ensureGameLoaded(gameId)`。** registry 的 resolver
 *    (`resolveNativeHelpComponent` / `resolveGameLocalConfig`) 都是**同步讀 reactive cache**,
 *    而 cache 只有 `ensureGameLoaded()` 會填。這支是跟 App.vue 平行的另一個 root,
 *    不會經過 `useGameId.fetchGameCode()` —— 少了這行,cache 恆空 →
 *    `historyComponent` 恆 undefined → **每一款遊戲都會顯示「此遊戲尚未支援盤面回放」**,
 *    而且 `resolveGameLocalConfig()?.gameCode` 也會恆 fallback 成 gameId。
 *    （registry.ts 檔頭的載入契約本來就寫了「replay 入口」要 await,先前漏實作。）
 */
import { computed, onMounted, ref } from 'vue'
import type { Component } from 'vue'
import { useI18n } from 'vue-i18n'
import { useHistoryStore } from '@/stores/history'
import {
  ensureGameLoaded,
  resolveNativeHelpComponent,
  resolveGameLocalConfig,
} from '@/games/registry'
import { isNativeHelpGame } from '@/config/nativeHelpGames'
import { fetchGameResult } from '@/api/game-frontend'
import { toUserMessage } from '@/api/errorMessage'
import type { FetchGameResultParams } from '@/api/game-frontend/types'
import { loadGameImagesPublic } from '@/composables/useGameImages'
import { applyLocaleFromUrl } from '@/lang'

// 語系:沿用 host 慣例,從 URL `l` / `lang` 寫進 i18n(replay 跳過登入,
// 所以要自己呼叫一次;同 App.vue)。回放連結帶 `&l=zh` 即可切換。
applyLocaleFromUrl()

const { t } = useI18n()

type Phase = 'loading' | 'ready' | 'unsupported' | 'error'

const phase = ref<Phase>('loading')
const errorMsg = ref('')

const historyStore = useHistoryStore()

/** 回放連結的原始 query（gameCode 不在這裡推導 —— 見 onMounted 的註解）。 */
type ReplayQuery = Omit<FetchGameResultParams, 'gameCode'> & { gameId: string }

/**
 * 解析回放連結參數。沿用既有 `?gameId=xxx` 慣例(跟正常進遊戲一致)。
 *
 * 🛑 **這裡不推導 `gameCode`**：它要走 `resolveGameLocalConfig()`，而那是同步讀 registry
 *    cache —— 必須等 `ensureGameLoaded()` 之後才有值。在這裡解會永遠拿到 fallback。
 */
function parseReplayParams(): ReplayQuery | null {
  const q = new URLSearchParams(window.location.search)
  const recordId = q.get('record_id') ?? ''
  const token = q.get('token') ?? ''
  const operatorId = q.get('operator_id') ?? ''
  const gameId = (q.get('gameId') ?? '').toLowerCase()
  if (!recordId || !token || !gameId) return null
  return { recordId, token, operatorId, gameId }
}

const gameId = ref<string>('')
/** 該遊戲的 history 元件(各遊戲自訂 / 共用),從 registry resolve */
const historyComponent = computed<Component | undefined>(() =>
  resolveNativeHelpComponent(gameId.value, 'history'),
)

onMounted(async () => {
  const p = parseReplayParams()
  if (!p) {
    phase.value = 'error'
    // 註：實際必填的是 gameId（不是 game_code）—— gameCode 由 registry 從 gameId 派生
    errorMsg.value = t('common.replay.missingParams')
    return
  }
  gameId.value = p.gameId

  // scope:先只支援 native games（= host 有建 src/games/<gameId>/content.ts）。
  // 這條查的是 build-time 靜態的 REGISTERED_GAME_IDS，不需要先載入。
  if (!isNativeHelpGame(p.gameId)) {
    phase.value = 'unsupported'
    return
  }

  // 🛑 先把該款的 per-game chunk + 共用 help modal 載進 registry cache，後面所有
  //    同步 resolver 才有東西可讀（見檔頭）。ensureGameLoaded 內部已吞掉單一 chunk
  //    失敗（只記 warn、不 reject），所以這裡不需要 try/catch。
  await ensureGameLoaded(p.gameId)

  // gameCode（API path 用）此刻才推導得到；查不到仍用 gameId 兜底
  const gameCode = resolveGameLocalConfig(p.gameId)?.gameCode || p.gameId

  // 回放沒有登入 → 圖片走 public 端點（公開、不帶 token）預載 blob，
  // 讓盤面 / WinSection 的 resolveImg('imgtag:..') 拿得到圖。fire-and-forget：
  // blob 是 reactive，載好會自動 pop 進畫面，不擋詳情渲染。
  // FetchGameResultParams.operatorId 為 number | string（API 契約）→ 圖片端點統一轉字串
  void loadGameImagesPublic(gameCode, { operatorId: String(p.operatorId) })

  try {
    // 後端回傳已是 BackendRecord 形狀 → 直接灌,不需 adapter
    const record = await fetchGameResult({ ...p, gameCode })
    // replay 模式:先開旗標(讓 HistoryDetailModal 跳過列表 watch、恆顯示 detail),
    // 再灌 store → HistoryDetail 直接吃 selectedRecord + gameId 渲染該局詳情
    historyStore.setReplayMode(true)
    historyStore.setGameId(p.gameId)
    historyStore.setSelectedRecord(record)
    // 已註冊卻解不出元件 = chunk 載入失敗，**不是**「這款不支援」→ 走 error 而非
    // unsupported，免得把網路問題顯示成產品限制（unsupported 只留給未註冊的 gameId）。
    if (historyComponent.value) {
      phase.value = 'ready'
    } else {
      phase.value = 'error'
      errorMsg.value = t('common.replay.refreshHint')
    }
  } catch (e) {
    phase.value = 'error'
    // ⚠️ 不可用 e.message —— 一次性 token 失效時那會是英文的 'Unauthorized'，
    //    直接顯示成「盤面載入失敗：Unauthorized」。
    errorMsg.value = toUserMessage(e)
  }
})

/** replay 沒有「上一頁列表」可回 → close 先當 no-op(或之後接關閉視窗) */
const onClose = (): void => {}
</script>

<template>
  <div class="result-detail-app">
    <div v-if="phase === 'loading'" class="result-state">{{ t('common.replay.loading') }}</div>

    <div v-else-if="phase === 'unsupported'" class="result-state">
      {{ t('common.replay.unsupported') }}
    </div>

    <div v-else-if="phase === 'error'" class="result-state">
      {{ t('common.replay.loadFailed') }}<span v-if="errorMsg">：{{ errorMsg }}</span>
    </div>

    <component
      :is="historyComponent"
      v-else-if="phase === 'ready' && historyComponent"
      :show="true"
      :modal-id="historyStore.selectedRecord?.rid ?? null"
      :game-id="gameId"
      @close="onClose"
    />
  </div>
</template>

<style scoped>
.result-detail-app {
  width: 100%;
  height: 100%;
}

.result-state {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: var(--color-theme, #fff);
  font-size: 1rem;
}
</style>
